import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    AlertTriangle, AlertCircle, Store, ImageIcon, X, Send, Clock, XCircle, MinusCircle,
} from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { EmptyState } from "@/components/common/EmptyState";
import { toast } from "sonner";
import { OPS } from "@/constants/testIds";
import QCViolationDetailDialog from "./QCViolationDetailDialog";

const STATUS_BADGE = {
    open: {
        cls: "bg-[color:rgba(232,168,80,0.20)] text-[color:var(--brand-brown)]",
        i18n: "qc_v_status_open",
    },
    in_progress: {
        cls: "bg-[color:rgba(83,143,181,0.22)] text-[color:var(--brand-brown)]",
        i18n: "qc_v_status_in_progress",
    },
    pending_approval: {
        cls: "bg-[color:rgba(147,112,219,0.18)] text-[color:#6d28d9]",
        i18n: "qc_v_status_pending_approval",
    },
    closed: {
        cls: "bg-[color:rgba(46,160,67,0.14)] text-[color:var(--success)]",
        i18n: "qc_v_status_closed",
    },
    overdue: {
        cls: "bg-[color:rgba(255,69,58,0.14)] text-[color:var(--danger)]",
        i18n: "qc_v_status_overdue",
    },
};

const SEV_TONE = {
    critical: "text-[color:var(--danger)]",
    moderate: "text-[color:var(--brand-orange)]",
    minor: "text-[color:var(--ink-muted)]",
};

/**
 * Violations list — cross-round, filterable, permission-scoped by the API.
 *
 * Clicking any row opens the detail dialog where status transitions and
 * before/after side-by-side viewing happen.
 */
export default function QCViolationsPage() {
    const { t, lang } = useI18n();
    const { isSuperAdmin } = useAuth();

    const [rows, setRows] = useState(null);
    const [loading, setLoading] = useState(true);
    const [branches, setBranches] = useState([]);
    const [sections, setSections] = useState([]);

    const [f, setF] = useState({
        branch_id: "all",
        status: "all",
        severity: "all",
        section_key: "all",
        overdue_only: false,
    });
    const [openViolationId, setOpenViolationId] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();

    // Deep-link from a notification: /quality?tab=violations&violation=<id>
    useEffect(() => {
        const vId = searchParams.get("violation");
        if (vId) {
            setOpenViolationId(vId);
            const next = new URLSearchParams(searchParams);
            next.delete("violation");
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    // Load small lookup lists once.
    useEffect(() => {
        let alive = true;
        Promise.all([
            api.get("/branches").then((r) => (r.data || []).filter((b) => b.is_active && !b.finance_only)),
            api.get("/qc-inspection/sections").then((r) => r.data || []),
        ])
            .then(([bs, ss]) => {
                if (!alive) return;
                setBranches(bs);
                setSections(ss);
            })
            .catch((e) => alive && toast.error(e?.response?.data?.detail || t("error_generic")));
        return () => { alive = false; };
    }, [t]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (f.branch_id !== "all") params.branch_id = f.branch_id;
            if (f.status !== "all") params.status = f.status;
            if (f.severity !== "all") params.severity = f.severity;
            if (f.section_key !== "all") params.section_key = f.section_key;
            if (f.overdue_only) params.overdue_only = "true";
            const r = await api.get("/qc-inspection/violations", { params });
            setRows(r.data || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        } finally {
            setLoading(false);
        }
    }, [f, t]);

    useEffect(() => { load(); }, [load]);

    const stats = useMemo(() => {
        const list = rows || [];
        return {
            total: list.length,
            open: list.filter((r) => r.effective_status === "open").length,
            in_progress: list.filter((r) => r.effective_status === "in_progress").length,
            pending_approval: list.filter((r) => r.effective_status === "pending_approval").length,
            overdue: list.filter((r) => r.effective_status === "overdue").length,
            closed: list.filter((r) => r.effective_status === "closed").length,
        };
    }, [rows]);

    const setFilter = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

    const activeFilterCount =
        (f.branch_id !== "all" ? 1 : 0) +
        (f.status !== "all" ? 1 : 0) +
        (f.severity !== "all" ? 1 : 0) +
        (f.section_key !== "all" ? 1 : 0) +
        (f.overdue_only ? 1 : 0);

    const clearFilters = () =>
        setF({ branch_id: "all", status: "all", severity: "all", section_key: "all", overdue_only: false });

    return (
        <div data-testid={OPS.qcIns.violations.page} className="space-y-4">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h2 className="text-base font-bold text-[color:var(--ink)]">{t("qc_v_title")}</h2>
                    <p className="text-xs text-[color:var(--ink-muted)] max-w-2xl">
                        {isSuperAdmin ? t("qc_v_desc_admin") : t("qc_v_desc_scoped")}
                    </p>
                </div>
                {activeFilterCount > 0 ? (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearFilters}
                        className="text-[color:var(--ink-muted)]"
                        data-testid={OPS.qcIns.violations.clearFilters}
                    >
                        <X className="h-4 w-4 me-1" />
                        {t("qc_v_clear_filters")}
                    </Button>
                ) : null}
            </div>

            {/* -------------- filter bar -------------- */}
            <div
                data-testid={OPS.qcIns.violations.filterBar}
                className="surface border border-[color:var(--border-soft)] rounded-xl p-3"
            >
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                    <div className="space-y-1">
                        <Label className="text-[11px] inline-flex items-center gap-1">
                            <Store className="h-3 w-3" />
                            {t("qc_v_filter_branch")}
                        </Label>
                        <Select value={f.branch_id} onValueChange={(v) => setFilter("branch_id", v)}>
                            <SelectTrigger data-testid={OPS.qcIns.violations.filterBranch} className="h-9">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t("qc_v_all")}</SelectItem>
                                {branches.map((b) => (
                                    <SelectItem key={b.id} value={b.id}>
                                        {lang === "ar" ? b.name_ar : (b.name_en || b.name_ar)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1">
                        <Label className="text-[11px]">{t("qc_v_filter_status")}</Label>
                        <Select value={f.status} onValueChange={(v) => setFilter("status", v)}>
                            <SelectTrigger data-testid={OPS.qcIns.violations.filterStatus} className="h-9">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t("qc_v_all")}</SelectItem>
                                <SelectItem value="open">{t("qc_v_status_open")}</SelectItem>
                                <SelectItem value="in_progress">{t("qc_v_status_in_progress")}</SelectItem>
                                <SelectItem value="pending_approval">{t("qc_v_status_pending_approval")}</SelectItem>
                                <SelectItem value="overdue">{t("qc_v_status_overdue")}</SelectItem>
                                <SelectItem value="closed">{t("qc_v_status_closed")}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1">
                        <Label className="text-[11px]">{t("qc_v_filter_severity")}</Label>
                        <Select value={f.severity} onValueChange={(v) => setFilter("severity", v)}>
                            <SelectTrigger data-testid={OPS.qcIns.violations.filterSeverity} className="h-9">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t("qc_v_all")}</SelectItem>
                                <SelectItem value="critical">{t("qc_ins_sev_critical")}</SelectItem>
                                <SelectItem value="moderate">{t("qc_ins_sev_moderate")}</SelectItem>
                                <SelectItem value="minor">{t("qc_ins_sev_minor")}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1">
                        <Label className="text-[11px]">{t("qc_v_filter_section")}</Label>
                        <Select value={f.section_key} onValueChange={(v) => setFilter("section_key", v)}>
                            <SelectTrigger data-testid={OPS.qcIns.violations.filterSection} className="h-9">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t("qc_v_all")}</SelectItem>
                                {sections.map((s) => (
                                    <SelectItem key={s.section_key} value={s.section_key}>
                                        {lang === "ar" ? s.title_ar : (s.title_en || s.title_ar)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex items-end gap-2 pb-1">
                        <div className="flex items-center gap-2 rounded-lg surface-2 border border-[color:var(--border-soft)] px-3 py-2 h-9 flex-1">
                            <Switch
                                data-testid={OPS.qcIns.violations.filterOverdue}
                                checked={f.overdue_only}
                                onCheckedChange={(v) => setFilter("overdue_only", v)}
                            />
                            <Label className="text-[11px] cursor-pointer">{t("qc_v_overdue_only")}</Label>
                        </div>
                    </div>
                </div>
            </div>

            {/* -------------- stat pills -------------- */}
            {rows !== null ? (
                <div
                    data-testid={OPS.qcIns.violations.statBar}
                    className="flex flex-wrap gap-2 text-[11px] font-medium"
                >
                    <StatPill label={t("qc_v_total")} value={stats.total} />
                    <StatPill label={t("qc_v_status_open")} value={stats.open} tone="warn" />
                    <StatPill label={t("qc_v_status_in_progress")} value={stats.in_progress} tone="info" />
                    <StatPill label={t("qc_v_status_pending_approval")} value={stats.pending_approval} tone="info" />
                    <StatPill label={t("qc_v_status_overdue")} value={stats.overdue} tone="danger" />
                    <StatPill label={t("qc_v_status_closed")} value={stats.closed} tone="ok" />
                </div>
            ) : null}

            {/* -------------- table -------------- */}
            {loading ? (
                <div className="space-y-2">
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                </div>
            ) : rows && rows.length === 0 ? (
                <EmptyState
                    icon={AlertTriangle}
                    title={t("qc_v_empty")}
                    hint={activeFilterCount > 0 ? t("qc_v_empty_filtered_hint") : t("qc_v_empty_hint")}
                    dataset={activeFilterCount > 0 ? undefined : "qc_violations"}
                />
            ) : (
                <div className="overflow-x-auto rounded-lg border border-[color:var(--border-soft)]">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-[color:rgba(224,222,217,0.5)] hover:bg-[color:rgba(224,222,217,0.5)]">
                                <TableHead className="text-start whitespace-nowrap">{t("qc_v_col_branch")}</TableHead>
                                <TableHead className="text-start">{t("qc_v_col_item")}</TableHead>
                                <TableHead className="text-start whitespace-nowrap">{t("qc_ins_severity")}</TableHead>
                                <TableHead className="text-start whitespace-nowrap">{t("qc_ins_responsible")}</TableHead>
                                <TableHead className="text-start whitespace-nowrap">{t("qc_ins_deadline")}</TableHead>
                                <TableHead className="text-start whitespace-nowrap">{t("qc_v_col_status")}</TableHead>
                                <TableHead className="text-end whitespace-nowrap">{t("qc_v_col_photos")}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(rows || []).map((r) => (
                                <ViolationRow
                                    key={r.id}
                                    r={r}
                                    lang={lang}
                                    t={t}
                                    onOpen={() => setOpenViolationId(r.id)}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            <QCViolationDetailDialog
                violationId={openViolationId}
                onOpenChange={(open) => !open && setOpenViolationId(null)}
                onChanged={load}
            />
        </div>
    );
}

// -------------- pieces --------------
function StatPill({ label, value, tone }) {
    const toneCls = {
        warn: "bg-[color:rgba(232,168,80,0.20)] text-[color:var(--brand-brown)]",
        info: "bg-[color:rgba(83,143,181,0.22)] text-[color:var(--brand-brown)]",
        danger: "bg-[color:rgba(255,69,58,0.14)] text-[color:var(--danger)]",
        ok: "bg-[color:rgba(46,160,67,0.14)] text-[color:var(--success)]",
    }[tone] || "bg-[color:rgba(224,222,217,0.5)] text-[color:var(--ink)]";
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 ${toneCls}`}>
            <span className="tnum font-semibold">{value}</span>
            <span className="text-[color:var(--ink-muted)]">{label}</span>
        </span>
    );
}

function HrPushBadge({ hr, vid, t }) {
    if (!hr || !hr.status) return null;
    const MAP = {
        sent: { Icon: Send, cls: "text-[color:var(--success)]", key: "qc_hr_push_sent" },
        queued: { Icon: Clock, cls: "text-[color:var(--ink-muted)]", key: "qc_hr_push_queued" },
        failed: { Icon: XCircle, cls: "text-[color:var(--danger)]", key: "qc_hr_push_failed" },
        skipped: { Icon: MinusCircle, cls: "text-[color:var(--ink-faint)]", key: "qc_hr_push_skipped" },
    };
    const meta = MAP[hr.status] || MAP.queued;
    const Icon = meta.Icon;
    const title = hr.reason || hr.last_error || "";
    return (
        <span
            data-testid={OPS.qcIns.violations.hrPush(vid)}
            data-hr-push-status={hr.status}
            title={title}
            className={`inline-flex items-center gap-1 text-[10px] font-medium ${meta.cls}`}
        >
            <Icon className="h-3 w-3" />
            {t(meta.key)}
            {hr.status === "failed" && hr.attempts ? <span className="tnum">({hr.attempts})</span> : null}
        </span>
    );
}

function ViolationRow({ r, lang, t, onOpen }) {
    const branch = lang === "ar" ? r.branch_name_ar : (r.branch_name_en || r.branch_name_ar);
    const badge = STATUS_BADGE[r.effective_status] || STATUS_BADGE.open;
    return (
        <TableRow
            data-testid={OPS.qcIns.violations.row(r.id)}
            role="button"
            tabIndex={0}
            onClick={onOpen}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen()}
            className="cursor-pointer hover:bg-[color:rgba(186,217,235,0.18)]"
        >
            <TableCell className="whitespace-nowrap font-medium text-[color:var(--ink)]">
                {branch || "—"}
            </TableCell>
            <TableCell>
                <div className="min-w-0">
                    <div className="text-xs text-[color:var(--ink-muted)] truncate">
                        {r.section_key.replace(/_/g, " ")}
                    </div>
                    <div className="text-sm text-[color:var(--ink)] font-medium leading-snug">
                        <bdi>{r.item}</bdi>
                    </div>
                </div>
            </TableCell>
            <TableCell className="whitespace-nowrap">
                <span className={`inline-flex items-center gap-1 ${SEV_TONE[r.severity] || ""}`}>
                    <AlertCircle className="h-3.5 w-3.5" />
                    {t(`qc_ins_sev_${r.severity}`)}
                </span>
            </TableCell>
            <TableCell className="whitespace-nowrap text-xs text-[color:var(--ink)]">
                <div className="flex flex-col gap-0.5">
                    <span>
                        {r.responsible || "—"}
                        {r.responsible && r.responsible_source === "manual" ? (
                            <span className="ms-1 text-[9px] text-[color:#b45309]">({t("qc_ins_responsible_unverified")})</span>
                        ) : null}
                    </span>
                    <HrPushBadge hr={r.hr_push} vid={r.id} t={t} />
                </div>
            </TableCell>
            <TableCell className="whitespace-nowrap text-xs">
                {r.deadline ? <bdi dir="ltr">{r.deadline}</bdi> : <span className="text-[color:var(--ink-muted)]">—</span>}
            </TableCell>
            <TableCell className="whitespace-nowrap">
                <Badge variant="secondary" className={`${badge.cls} border-0 text-[10px]`}>
                    {t(badge.i18n)}
                </Badge>
                {r.closed_without_proof ? (
                    <span
                        title={t("qc_v_closed_without_proof")}
                        className="ms-1 text-[10px] text-[color:var(--danger)]"
                    >
                        ⚠
                    </span>
                ) : null}
            </TableCell>
            <TableCell className="whitespace-nowrap text-end">
                <span className="inline-flex items-center gap-1 text-xs text-[color:var(--ink-muted)]">
                    <ImageIcon className="h-3.5 w-3.5" />
                    <span className="tnum">
                        {r.photos_before}
                        <span className="mx-0.5">/</span>
                        {r.photos_after}
                    </span>
                </span>
            </TableCell>
        </TableRow>
    );
}
